import { Menu } from '@mantine/core';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialogModal } from 'shared/ui/Modals/ConfirmationDialogModal';
import type { UseBoardPreferenceAdminResult } from '../../model/use-board-preference-admin';

export type UsePreferenceAdminActionsArgs<T extends Record<string, unknown>> = {
  /** Current effective value to push as the new board default. */
  currentValue: T;
  /** Whether a board layer already exists (controls "Reset" visibility). */
  hasBoardDefault: boolean;
  admin: UseBoardPreferenceAdminResult<T>;
};

export type UsePreferenceAdminActionsResult = {
  /**
   * Render this inside the Mantine `Menu.Dropdown`. Items only trigger
   * confirm-modal state — they do NOT own the modal itself.
   */
  menuItems: ReactElement;
  /**
   * Render this OUTSIDE the Mantine Menu (typically as a sibling of the
   * `<Menu>` element). The confirmation modals live here so they survive
   * the menu closing on item click — otherwise the modal would unmount
   * together with the dropdown contents and never become visible.
   */
  confirmModals: ReactElement;
};

/**
 * Headless controller for admin-only "Set as board default" / "Reset board
 * default" actions on a single preference key. Splits rendering across two
 * surfaces (menu items + modals) so neither lives inside the dismissing
 * dropdown, fixing the "modal flashes and disappears" bug that arises when
 * confirmation state lives in a component nested under Mantine `Menu.Dropdown`.
 */
export const usePreferenceAdminActions = <T extends Record<string, unknown>>({
  currentValue,
  hasBoardDefault,
  admin,
}: UsePreferenceAdminActionsArgs<T>): UsePreferenceAdminActionsResult => {
  const { t } = useTranslation('common');
  const [setConfirmOpened, setSetConfirmOpened] = useState(false);
  const [resetConfirmOpened, setResetConfirmOpened] = useState(false);

  return {
    menuItems: (
      <>
        <Menu.Item
          data-testid="preferences-set-board-default"
          onClick={() => setSetConfirmOpened(true)}
          disabled={admin.isMutating}
        >
          {t('preferences.setBoardDefault')}
        </Menu.Item>
        {hasBoardDefault && (
          <Menu.Item
            data-testid="preferences-reset-board-default"
            onClick={() => setResetConfirmOpened(true)}
            disabled={admin.isMutating}
          >
            {t('preferences.resetBoardDefault')}
          </Menu.Item>
        )}
      </>
    ),
    confirmModals: (
      <>
        <ConfirmationDialogModal
          opened={setConfirmOpened}
          onClose={() => setSetConfirmOpened(false)}
          title={t('preferences.setBoardDefaultConfirmTitle')}
          message={t('preferences.setBoardDefaultConfirmMessage')}
          confirmButtonText="preferences.setBoardDefaultConfirmCta"
          onConfirm={() => admin.setBoardDefault(currentValue)}
        />
        <ConfirmationDialogModal
          opened={resetConfirmOpened}
          onClose={() => setResetConfirmOpened(false)}
          title={t('preferences.resetBoardDefaultConfirmTitle')}
          message={t('preferences.resetBoardDefaultConfirmMessage')}
          confirmButtonText="preferences.resetBoardDefaultConfirmCta"
          variant="danger"
          onConfirm={() => admin.resetBoardDefault()}
        />
      </>
    ),
  };
};
